import { useFacilityFilterOptions } from '@/hooks/useFacilityFilterOptions'
import { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { ColorScheme } from '@hivewatch/dark'
import { Flex } from '@hivewatch/dark'

import {
  MultiSelectFilterField,
  SelectFilterField,
} from '@/components/filter-drawer'
import { Action, FacilityType } from '@/graphql/generated/schemas'
import { FilterOptionI } from '@/utils/filterDrawers'
import { getFacilityNoOptionsMessage } from '@/utils/forms/validation'

import { getTranslatedActionText } from '../../../utils/ruleRows'
import type { RulesFilterFormInputsI } from '../types/form'

interface DarkRulesFilterFormIProps {
  control: Control<RulesFilterFormInputsI>
  colorScheme: ColorScheme
}

export const DarkRulesFilterForm = ({
  control,
  colorScheme,
}: DarkRulesFilterFormIProps) => {
  const { t } = useTranslation()

  const {
    facilityOptions: facilityOptionsWithCue,
    isFacilitiesLoading,
    isFacilitiesError,
    facilitySearch: facilityName,
    setFacilitySearch: setFacilityName,
  } = useFacilityFilterOptions({ filter: { type: FacilityType.Normal } })

  const actionOptions: FilterOptionI<Action>[] = [
    { label: getTranslatedActionText(t, Action.Mute), value: Action.Mute },
    {
      label: getTranslatedActionText(t, Action.SendEmail),
      value: Action.SendEmail,
    },
    {
      label: getTranslatedActionText(t, Action.EnableAiOperator),
      value: Action.EnableAiOperator,
    },
    {
      label: getTranslatedActionText(t, Action.SetPriority),
      value: Action.SetPriority,
    },
  ]

  const statusOptions: FilterOptionI<boolean>[] = [
    { label: t('automations:filter_status_on'), value: true },
    { label: t('automations:filter_status_off'), value: false },
  ]

  return (
    <Flex direction='column' gap='16px'>
      <MultiSelectFilterField
        colorScheme={colorScheme}
        control={control}
        data-testid='incidentRulesPage_filterDrawer_facility'
        isLoading={isFacilitiesLoading}
        label={t('common:facility')}
        name='facilityIds'
        noOptionsMessage={({ inputValue }) =>
          getFacilityNoOptionsMessage(
            inputValue,
            isFacilitiesError,
            t('common:facility_search_error')
          )
        }
        onSearchChange={setFacilityName}
        options={facilityOptionsWithCue}
        placeholder={t('automations:filter_facility_placeholder')}
        preserveOptionOrder
        searchValue={facilityName}
      />
      <MultiSelectFilterField
        colorScheme={colorScheme}
        control={control}
        data-testid='incidentRulesPage_filterDrawer_action'
        label={t('automations:filter_action_label')}
        name='actions'
        options={actionOptions}
        placeholder={t('automations:filter_action_placeholder')}
      />
      <SelectFilterField
        colorScheme={colorScheme}
        control={control}
        data-testid='incidentRulesPage_filterDrawer_status'
        label={t('common:status')}
        name='status'
        options={statusOptions}
        placeholder={t('automations:filter_status_placeholder')}
      />
    </Flex>
  )
}
